// Shared option lists
export const SERVICE_LANES = [
  { value: "sell_home", label: "Selling Mom's Home" },
  { value: "senior_placement", label: "Senior Living Placement" },
  { value: "home_safety", label: "Keep Mama Home Safely" },
  { value: "move_into_my_home", label: "Move Mama Into My Home" },
  { value: "downsizing", label: "Downsizing & Estate" },
  { value: "home_repairs", label: "Home Repairs & Accessibility" },
  { value: "partner_referral", label: "Partner / Vendor Referral" },
];

const opt = (arr) => arr.map((v) => ({ value: v.toLowerCase().replace(/[\s/]+/g, "_"), label: v }));

// Relation label keys
export const RELATION_LABELS = {
  branches: "name",
  families: "family_name",
  cases: "title",
  loved_ones: "name",
  partners: "name",
  users: "full_name",
  leads: "name",
};

// Resource configurations
export const RESOURCES = {
  leads: {
    key: "leads", path: "/leads", title: "Leads", singular: "Lead", icon: "UserPlus",
    subtitle: "Incoming families and intake pipeline",
    statusKey: "status",
    columns: [
      { key: "name", label: "Family / Contact", primary: true },
      { key: "senior_name", label: "Loved One" },
      { key: "phone", label: "Phone" },
      { key: "source", label: "Source" },
      { key: "status", label: "Status", type: "badge" },
      { key: "created_at", label: "Added", type: "date" },
    ],
    filters: [{ key: "status", label: "Status", options: opt(["new", "contacted", "qualified", "consultation_booked", "converted", "lost"]) }],
    fields: [
      { key: "name", label: "Family / Contact Name", type: "text", required: true, span: 2 },
      { key: "contact_name", label: "Primary Contact", type: "text" },
      { key: "senior_name", label: "Loved One (Mama) Name", type: "text" },
      { key: "email", label: "Email", type: "email" },
      { key: "phone", label: "Phone", type: "tel" },
      { key: "source", label: "Lead Source", type: "select", options: opt(["Website", "Referral", "Phone", "Event", "Trilogy", "Partner", "Other"]) },
      { key: "status", label: "Status", type: "select", options: opt(["new", "contacted", "qualified", "consultation_booked", "converted", "lost"]), default: "new" },
      { key: "service_interest", label: "Service Interest", type: "multiselect", options: SERVICE_LANES, span: 2 },
      { key: "branch_id", label: "Branch", type: "relation", resource: "branches", superOnly: true },
      { key: "notes", label: "Intake Notes", type: "textarea", span: 2 },
    ],
    convert: true,
  },
  consultations: {
    key: "consultations", path: "/consultations", title: "Consultations", singular: "Consultation", icon: "CalendarHeart",
    subtitle: "Free one-hour family transition consultations",
    statusKey: "status",
    columns: [
      { key: "family_name", label: "Family", primary: true },
      { key: "scheduled_at", label: "When", type: "datetime" },
      { key: "mode", label: "Mode" },
      { key: "status", label: "Status", type: "badge" },
    ],
    filters: [{ key: "status", label: "Status", options: opt(["scheduled", "completed", "cancelled", "no_show"]) }],
    fields: [
      { key: "family_name", label: "Family Name", type: "text", required: true, span: 2 },
      { key: "lead_id", label: "From Lead", type: "relation", resource: "leads" },
      { key: "family_id", label: "Family", type: "relation", resource: "families" },
      { key: "scheduled_at", label: "Scheduled Date/Time", type: "datetime-local", required: true },
      { key: "duration_min", label: "Duration (min)", type: "number", default: 60 },
      { key: "mode", label: "Mode", type: "select", options: opt(["In-person", "Phone", "Video"]), default: "in-person" },
      { key: "location", label: "Location", type: "text" },
      { key: "consultant_id", label: "Consultant", type: "relation", resource: "users" },
      { key: "status", label: "Status", type: "select", options: opt(["scheduled", "completed", "cancelled", "no_show"]), default: "scheduled" },
      { key: "outcome", label: "Outcome", type: "textarea", span: 2 },
      { key: "follow_up_at", label: "Follow-up Date", type: "date" },
      { key: "branch_id", label: "Branch", type: "relation", resource: "branches", superOnly: true },
      { key: "notes", label: "Notes", type: "textarea", span: 2 },
    ],
  },
  cases: {
    key: "cases", path: "/cases", title: "Cases", singular: "Case", icon: "FolderHeart",
    subtitle: "Active family transition cases by service lane",
    statusKey: "status",
    columns: [
      { key: "case_number", label: "Case #" },
      { key: "title", label: "Title", primary: true },
      { key: "service_lane", label: "Lane", type: "lane" },
      { key: "priority", label: "Priority", type: "badge" },
      { key: "status", label: "Status", type: "badge" },
    ],
    filters: [
      { key: "service_lane", label: "Service Lane", options: SERVICE_LANES },
      { key: "status", label: "Status", options: opt(["open", "in_progress", "on_hold", "completed", "closed"]) },
    ],
    fields: [
      { key: "title", label: "Case Title", type: "text", required: true, span: 2 },
      { key: "case_number", label: "Case Number", type: "text" },
      { key: "family_id", label: "Family", type: "relation", resource: "families" },
      { key: "loved_one_id", label: "Loved One", type: "relation", resource: "loved_ones" },
      { key: "service_lane", label: "Service Lane", type: "select", options: SERVICE_LANES, required: true },
      { key: "status", label: "Status", type: "select", options: opt(["open", "in_progress", "on_hold", "completed", "closed"]), default: "open" },
      { key: "priority", label: "Priority", type: "select", options: opt(["low", "medium", "high"]), default: "medium" },
      { key: "owner_id", label: "Case Manager", type: "relation", resource: "users" },
      { key: "follow_up_at", label: "Follow-up Date", type: "date" },
      { key: "branch_id", label: "Branch", type: "relation", resource: "branches", superOnly: true },
      { key: "summary", label: "Summary", type: "textarea", span: 2 },
    ],
  },
  families: {
    key: "families", path: "/families", title: "Families", singular: "Family", icon: "Home",
    subtitle: "Family accounts and primary contacts", portalInvite: "family",
    statusKey: "status",
    columns: [
      { key: "family_name", label: "Family", primary: true },
      { key: "primary_contact_name", label: "Primary Contact" },
      { key: "primary_contact_phone", label: "Phone" },
      { key: "status", label: "Status", type: "badge" },
    ],
    filters: [{ key: "status", label: "Status", options: opt(["active", "inactive", "archived"]) }],
    fields: [
      { key: "family_name", label: "Family Name", type: "text", required: true, span: 2 },
      { key: "primary_contact_name", label: "Primary Contact", type: "text" },
      { key: "primary_contact_phone", label: "Phone", type: "tel" },
      { key: "primary_contact_email", label: "Email", type: "email" },
      { key: "decision_maker", label: "Decision Maker", type: "text" },
      { key: "address", label: "Address", type: "text", span: 2 },
      { key: "status", label: "Status", type: "select", options: opt(["active", "inactive", "archived"]), default: "active" },
      { key: "branch_id", label: "Branch", type: "relation", resource: "branches", superOnly: true },
      { key: "notes", label: "Notes", type: "textarea", span: 2 },
    ],
  },
  loved_ones: {
    key: "loved_ones", path: "/loved-ones", title: "Loved Ones", singular: "Loved One", icon: "HeartHandshake",
    subtitle: "Senior profiles and care needs",
    statusKey: "care_level",
    columns: [
      { key: "name", label: "Name", primary: true },
      { key: "age", label: "Age" },
      { key: "current_living_situation", label: "Living Situation" },
      { key: "care_level", label: "Care Level", type: "badge" },
    ],
    filters: [{ key: "care_level", label: "Care Level", options: opt(["independent", "light_support", "assisted", "memory_care", "skilled"]) }],
    fields: [
      { key: "name", label: "Name", type: "text", required: true, span: 2 },
      { key: "family_id", label: "Family", type: "relation", resource: "families" },
      { key: "age", label: "Age", type: "number" },
      { key: "dob", label: "Date of Birth", type: "date" },
      { key: "current_living_situation", label: "Current Living Situation", type: "text", span: 2 },
      { key: "care_level", label: "Care Level", type: "select", options: opt(["independent", "light_support", "assisted", "memory_care", "skilled"]) },
      { key: "mobility", label: "Mobility", type: "select", options: opt(["independent", "cane", "walker", "wheelchair", "bedbound"]) },
      { key: "cognitive_status", label: "Cognitive Status", type: "text" },
      { key: "budget", label: "Monthly Budget ($)", type: "number" },
      { key: "care_needs", label: "Care Needs", type: "multiselect", options: opt(["Medication", "Bathing", "Meals", "Mobility", "Memory", "Companionship", "Transportation"]), span: 2 },
      { key: "preferences", label: "Preferences", type: "textarea", span: 2 },
      { key: "branch_id", label: "Branch", type: "relation", resource: "branches", superOnly: true },
    ],
  },
  properties: {
    key: "properties", path: "/properties", title: "Properties", singular: "Property", icon: "Building2",
    subtitle: "Home sale records (Selling Mom's Home)", lane: "sell_home",
    statusKey: "status",
    columns: [
      { key: "address", label: "Address", primary: true },
      { key: "city", label: "City" },
      { key: "list_price", label: "List Price", type: "currency" },
      { key: "purchaser_visible", label: "Investor", type: "badge" },
      { key: "status", label: "Status", type: "badge" },
    ],
    filters: [{ key: "status", label: "Status", options: opt(["assessment", "prep", "listed", "under_contract", "sold"]) }],
    fields: [
      { key: "address", label: "Address", type: "text", required: true, span: 2 },
      { key: "city", label: "City", type: "text" },
      { key: "state", label: "State", type: "text" },
      { key: "zip", label: "Zip", type: "text" },
      { key: "case_id", label: "Case", type: "relation", resource: "cases" },
      { key: "family_id", label: "Family", type: "relation", resource: "families" },
      { key: "beds", label: "Beds", type: "number" },
      { key: "baths", label: "Baths", type: "number" },
      { key: "sqft", label: "Sq Ft", type: "number" },
      { key: "list_price", label: "List Price ($)", type: "number" },
      { key: "sale_price", label: "Sale Price ($)", type: "number" },
      { key: "status", label: "Status", type: "select", options: opt(["assessment", "prep", "listed", "under_contract", "sold"]), default: "assessment" },
      { key: "agent_id", label: "Listing Agent (Partner)", type: "relation", resource: "partners" },
      { key: "purchaser_visible", label: "Visible to Investors", type: "select", options: [{ value: "yes", label: "Yes" }, { value: "no", label: "No" }], default: "no" },
      { key: "branch_id", label: "Branch", type: "relation", resource: "branches", superOnly: true },
      { key: "notes", label: "Notes", type: "textarea", span: 2 },
    ],
  },
  placements: {
    key: "placements", path: "/placement", title: "Placement", singular: "Placement", icon: "BedDouble",
    subtitle: "Senior living placement records", lane: "senior_placement",
    statusKey: "status",
    columns: [
      { key: "community_name", label: "Community", primary: true },
      { key: "care_type", label: "Care Type", type: "badge" },
      { key: "monthly_cost", label: "Monthly", type: "currency" },
      { key: "status", label: "Status", type: "badge" },
    ],
    filters: [{ key: "status", label: "Status", options: opt(["researching", "touring", "applied", "placed", "declined"]) }],
    fields: [
      { key: "community_name", label: "Community Name", type: "text", required: true, span: 2 },
      { key: "loved_one_id", label: "Loved One", type: "relation", resource: "loved_ones" },
      { key: "case_id", label: "Case", type: "relation", resource: "cases" },
      { key: "care_type", label: "Care Type", type: "select", options: opt(["independent", "assisted_living", "memory_care", "skilled_nursing", "in_home"]) },
      { key: "monthly_cost", label: "Monthly Cost ($)", type: "number" },
      { key: "move_in_date", label: "Move-in Date", type: "date" },
      { key: "status", label: "Status", type: "select", options: opt(["researching", "touring", "applied", "placed", "declined"]), default: "researching" },
      { key: "partner_id", label: "Community (Partner)", type: "relation", resource: "partners" },
      { key: "branch_id", label: "Branch", type: "relation", resource: "branches", superOnly: true },
      { key: "notes", label: "Notes", type: "textarea", span: 2 },
    ],
  },
  home_safety: {
    key: "home_safety", path: "/home-safety", title: "Home Safety", singular: "Home Safety Plan", icon: "ShieldCheck",
    subtitle: "Keep Mama home safely — assessments & modifications", lane: "home_safety",
    statusKey: "status",
    columns: [
      { key: "address", label: "Address", primary: true },
      { key: "loved_one_name", label: "Loved One" },
      { key: "cost_estimate", label: "Estimate", type: "currency" },
      { key: "status", label: "Status", type: "badge" },
    ],
    filters: [{ key: "status", label: "Status", options: opt(["assessment", "scheduled", "in_progress", "completed"]) }],
    fields: [
      { key: "address", label: "Home Address", type: "text", required: true, span: 2 },
      { key: "loved_one_name", label: "Loved One", type: "text" },
      { key: "loved_one_id", label: "Loved One Record", type: "relation", resource: "loved_ones" },
      { key: "case_id", label: "Case", type: "relation", resource: "cases" },
      { key: "modifications", label: "Modifications Needed", type: "multiselect", options: opt(["Grab Bars", "Stair Rail", "Ramp", "Walk-in Shower", "Lighting", "Non-slip Flooring", "Medical Alert", "Door Widening"]), span: 2 },
      { key: "cost_estimate", label: "Cost Estimate ($)", type: "number" },
      { key: "status", label: "Status", type: "select", options: opt(["assessment", "scheduled", "in_progress", "completed"]), default: "assessment" },
      { key: "partner_id", label: "Contractor (Partner)", type: "relation", resource: "partners" },
      { key: "branch_id", label: "Branch", type: "relation", resource: "branches", superOnly: true },
      { key: "notes", label: "Assessment Notes", type: "textarea", span: 2 },
    ],
  },
  move_mama: {
    key: "move_mama", path: "/move-mama-into-my-home", title: "Move Mama Into My Home", singular: "Move-in Plan", icon: "Users",
    subtitle: "Moving Mama into a family member's home", lane: "move_into_my_home",
    statusKey: "status",
    columns: [
      { key: "host_name", label: "Host Family", primary: true },
      { key: "loved_one_name", label: "Loved One" },
      { key: "status", label: "Status", type: "badge" },
    ],
    filters: [{ key: "status", label: "Status", options: opt(["planning", "prep", "in_progress", "completed"]) }],
    fields: [
      { key: "host_name", label: "Host Family / Member", type: "text", required: true, span: 2 },
      { key: "loved_one_name", label: "Loved One", type: "text" },
      { key: "loved_one_id", label: "Loved One Record", type: "relation", resource: "loved_ones" },
      { key: "case_id", label: "Case", type: "relation", resource: "cases" },
      { key: "room_prep", label: "Room Prep Items", type: "multiselect", options: opt(["Bedroom Setup", "Bathroom Mods", "Ramp", "Furniture", "Medical Equipment", "Storage"]), span: 2 },
      { key: "status", label: "Status", type: "select", options: opt(["planning", "prep", "in_progress", "completed"]), default: "planning" },
      { key: "branch_id", label: "Branch", type: "relation", resource: "branches", superOnly: true },
      { key: "notes", label: "Notes", type: "textarea", span: 2 },
    ],
  },
  downsizing: {
    key: "downsizing", path: "/downsizing", title: "Downsizing", singular: "Downsizing Job", icon: "PackageOpen",
    subtitle: "Belongings, cleanout, moving, auction & estate support", lane: "downsizing",
    statusKey: "status",
    columns: [
      { key: "family_name", label: "Family", primary: true },
      { key: "service_type", label: "Service", type: "badge" },
      { key: "scheduled_date", label: "Scheduled", type: "date" },
      { key: "status", label: "Status", type: "badge" },
    ],
    filters: [
      { key: "service_type", label: "Service", options: opt(["declutter", "cleanout", "auction", "estate_sale", "moving", "donation"]) },
      { key: "status", label: "Status", options: opt(["scheduled", "in_progress", "completed"]) },
    ],
    fields: [
      { key: "family_name", label: "Family Name", type: "text", required: true, span: 2 },
      { key: "family_id", label: "Family", type: "relation", resource: "families" },
      { key: "case_id", label: "Case", type: "relation", resource: "cases" },
      { key: "service_type", label: "Service Type", type: "select", options: opt(["declutter", "cleanout", "auction", "estate_sale", "moving", "donation"]) },
      { key: "scheduled_date", label: "Scheduled Date", type: "date" },
      { key: "estimated_value", label: "Estimated Value ($)", type: "number" },
      { key: "status", label: "Status", type: "select", options: opt(["scheduled", "in_progress", "completed"]), default: "scheduled" },
      { key: "partner_id", label: "Vendor (Partner)", type: "relation", resource: "partners" },
      { key: "branch_id", label: "Branch", type: "relation", resource: "branches", superOnly: true },
      { key: "notes", label: "Notes", type: "textarea", span: 2 },
    ],
  },
  partners: {
    key: "partners", path: "/partners", title: "Partners", singular: "Partner", icon: "Handshake",
    subtitle: "Partner & vendor referral network", portalInvite: "partner",
    statusKey: "status",
    columns: [
      { key: "name", label: "Name", primary: true },
      { key: "category", label: "Category", type: "badge" },
      { key: "phone", label: "Phone" },
      { key: "status", label: "Status", type: "badge" },
    ],
    filters: [
      { key: "category", label: "Category", options: opt(["realtor", "mover", "contractor", "senior_community", "auctioneer", "estate_sale", "home_health", "financial", "legal", "other"]) },
      { key: "status", label: "Status", options: opt(["active", "inactive", "pending"]) },
    ],
    fields: [
      { key: "name", label: "Partner Name", type: "text", required: true, span: 2 },
      { key: "company", label: "Company", type: "text" },
      { key: "category", label: "Category", type: "select", options: opt(["realtor", "mover", "contractor", "senior_community", "auctioneer", "estate_sale", "home_health", "financial", "legal", "other"]) },
      { key: "contact_name", label: "Contact Person", type: "text" },
      { key: "phone", label: "Phone", type: "tel" },
      { key: "email", label: "Email", type: "email" },
      { key: "address", label: "Address", type: "text", span: 2 },
      { key: "referral_fee_pct", label: "Referral Fee (%)", type: "number" },
      { key: "rating", label: "Rating (1-5)", type: "number" },
      { key: "status", label: "Status", type: "select", options: opt(["active", "inactive", "pending"]), default: "active" },
      { key: "branch_id", label: "Branch", type: "relation", resource: "branches", superOnly: true },
      { key: "notes", label: "Notes", type: "textarea", span: 2 },
    ],
  },
  trilogy_outreach: {
    key: "trilogy_outreach", path: "/trilogy-outreach", title: "Trilogy Outreach", singular: "Outreach", icon: "Dumbbell",
    subtitle: "Trilogy Sports & Fitness outreach and partnerships",
    statusKey: "status",
    columns: [
      { key: "contact_name", label: "Contact", primary: true },
      { key: "organization", label: "Organization" },
      { key: "type", label: "Type", type: "badge" },
      { key: "status", label: "Status", type: "badge" },
    ],
    filters: [
      { key: "type", label: "Type", options: opt(["event", "partnership", "referral_program", "sponsorship", "class"]) },
      { key: "status", label: "Status", options: opt(["prospect", "contacted", "engaged", "active", "declined"]) },
    ],
    fields: [
      { key: "contact_name", label: "Contact Name", type: "text", required: true, span: 2 },
      { key: "organization", label: "Organization", type: "text", default: "Trilogy Sports and Fitness" },
      { key: "type", label: "Type", type: "select", options: opt(["event", "partnership", "referral_program", "sponsorship", "class"]) },
      { key: "status", label: "Status", type: "select", options: opt(["prospect", "contacted", "engaged", "active", "declined"]), default: "prospect" },
      { key: "scheduled_date", label: "Date", type: "date" },
      { key: "owner_id", label: "Owner", type: "relation", resource: "users" },
      { key: "branch_id", label: "Branch", type: "relation", resource: "branches", superOnly: true },
      { key: "notes", label: "Notes", type: "textarea", span: 2 },
    ],
  },
  tasks: {
    key: "tasks", path: "/tasks", title: "Tasks", singular: "Task", icon: "ListChecks",
    subtitle: "Follow-ups and to-dos across the team",
    statusKey: "status",
    columns: [
      { key: "title", label: "Task", primary: true },
      { key: "priority", label: "Priority", type: "badge" },
      { key: "due_date", label: "Due", type: "date" },
      { key: "status", label: "Status", type: "badge" },
    ],
    filters: [
      { key: "status", label: "Status", options: opt(["todo", "in_progress", "done"]) },
      { key: "priority", label: "Priority", options: opt(["low", "medium", "high"]) },
    ],
    fields: [
      { key: "title", label: "Task Title", type: "text", required: true, span: 2 },
      { key: "description", label: "Description", type: "textarea", span: 2 },
      { key: "assignee_id", label: "Assignee", type: "relation", resource: "users" },
      { key: "due_date", label: "Due Date", type: "date" },
      { key: "priority", label: "Priority", type: "select", options: opt(["low", "medium", "high"]), default: "medium" },
      { key: "status", label: "Status", type: "select", options: opt(["todo", "in_progress", "done"]), default: "todo" },
      { key: "related_type", label: "Related To (type)", type: "select", options: opt(["case", "lead", "family", "consultation", "property", "placement"]) },
      { key: "branch_id", label: "Branch", type: "relation", resource: "branches", superOnly: true },
    ],
  },
};

export const RESOURCE_BY_PATH = Object.values(RESOURCES).reduce((acc, r) => { acc[r.path] = r; return acc; }, {});
